import type { BridgeInput, BridgeMethod, RuntimeMessage } from "../shared/frames";
import { iconFromApp } from "../shared/icons";
import { attempt } from "../shared/quietly";
import type { RenderInput } from "../shared/views/render";
import { type Selection, type TemplateInfo, ViewConnection, type ViewCredentials } from "./connection";
import { DataHost } from "./data-host";
import type { HostPresentation, NavEntry, Reach } from "./frame/protocol";
import { GivenData } from "./given-data";
import { controlsLook, LocalView, type PageContext, ViewRenderer, viewTitle } from "./renderer";
import { readJSON, writeJSON } from "./storage";

declare global {
  interface Window {
    __coastNative?: boolean;
    __coastOverlayBottom?: number;
    __coastOverlayTop?: number;
    __coastEditing?: boolean;
    webkit?: { messageHandlers?: { runtime?: { postMessage: (message: RuntimeMessage) => void } } };
    /** The bridge the Coast app drives the runtime through: every method of the contract, and no other. */
    coastRuntime: Record<BridgeMethod, (...args: never[]) => unknown>;
  }
}
const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const renderer = new ViewRenderer($<HTMLIFrameElement>("page-frame"), $("selection-layer"));
const panel = $<HTMLDialogElement>("panel");
let connection: ViewConnection | null = null;
/** The open view's data, and with it its manifest. */
let data: DataHost | null = null;
let templates: TemplateInfo[] = [];
let selected: Selection | null = null;
let nativeHost = Boolean(window.__coastNative);
let panelKind = "";
let startup = 0;
/** Whether the open view came with a ticket, so it is not saved or shared as a link. */
let opened = false;
/** A ticket the Coast app sent before the view it opens, kept for it. */
let earlyTicket: { view: string; ticket: string } | null = null;
/**
 * What the Coast app said about the view it opened: its chat's title and icon, its organization, the sections to open
 * at, and how it presents a section opened deeper, as a screen of its own or a sheet.
 */
let openedWith: {
  chat: PageContext["chat"];
  org: PageContext["org"];
  nav: NavEntry[];
  presents: HostPresentation[];
  home?: { name: string; chats: NonNullable<PageContext["chats"]> };
  /** `posted` for a view in a message (docs/views.md#15-views-in-messages), which reaches less than a pill. */
  reach?: Reach;
  /** The person's own Agent, where they have one to ask (`coast.agent`). */
  agent?: { name: string };
} = { chat: null, org: null, nav: [], presents: [] };
let toastTimer: ReturnType<typeof setTimeout>;
let editing = Boolean(window.__coastEditing);
type SavedView = ViewCredentials & { updatedAt?: string };
let statusQueued = false;
const colors = ["#bddaaa", "#d8bfea", "#a9d7e3", "#eec397", "#e8abc1"];
const readStored = <T>(key: string, fallback: T): T => readJSON<T>(key) ?? fallback;
const store = writeJSON;
const identity = readStored<{ id?: string; name: string; color: string }>("coast:identity", {
  name: `Guest ${Math.floor(100 + Math.random() * 900)}`,
  color: colors[Math.floor(Math.random() * colors.length)] ?? "#bddaaa",
}) as { id: string; name: string; color: string };
// A stable id names this person to the view's agent, so their runs reach their devices after a reconnect.
if (!identity.id) identity.id = crypto.randomUUID().replace(/-/g, "");
store("coast:identity", identity);
/** To the Coast app around the runtime, when there is one. */
function native(message: RuntimeMessage) {
  attempt(() => window.webkit?.messageHandlers?.runtime?.postMessage(message));
}

function setNativeHost(value = true) {
  nativeHost = value;
  document.body.classList.toggle("native-host", value);
  if (value && typeof window.__coastOverlayBottom === "number") setOverlayBottom(window.__coastOverlayBottom);
  updateTopClearance();
  notifyStatus();
  publishLibrary();
}
function setOverlayTop(value: number) {
  const px = Math.max(0, Math.min(2000, Number(value) || 0));
  window.__coastOverlayTop = px;
  document.documentElement.style.setProperty("--overlay-top", `${px}px`);
  renderer.setOverlayTop(px);
}
function updateTopClearance() {
  if (nativeHost) setOverlayTop(window.__coastOverlayTop || 0);
  else setOverlayTop(Math.max(0, $("toolbar").getBoundingClientRect().bottom));
}
/** What the Coast app's controls cover of the page's foot, the home indicator's room included, editing or not. */
function setOverlayBottom(value: number) {
  const px = Math.max(0, Math.min(2000, Number(value) || 0));
  window.__coastOverlayBottom = px;
  document.documentElement.style.setProperty("--overlay-bottom", `${px}px`);
  renderer.setOverlayBottom(px);
}
function setEditing(value: boolean) {
  editing = Boolean(value);
  window.__coastEditing = editing;
  document.body.classList.toggle("editing", editing);
  if (!editing) {
    setPicking(false);
    select(null);
    connection?.editing(false);
  }
  notifyStatus();
  publishLibrary();
  return { editing };
}
function metricsValue(value: number | null | undefined) {
  return value == null ? "—" : `${Math.round(value)} ms`;
}
function notifyStatus() {
  if (statusQueued) return;
  statusQueued = true;
  requestAnimationFrame(() => {
    statusQueued = false;
    native(connectionStatus());
  });
}
function connectionStatus() {
  return {
    type: "view.status",
    editing,
    appearance: controlsLook(data?.manifest().appearance, renderer.appearance),
    background: renderer.background,
    connected: connection?.connected || false,
    viewId: connection?.credentials.viewId || null,
    // Who this device is in the view, so the shell can tell its own messages in the shared chat from others'.
    personId: connection?.identity.id ?? identity.id,
    layout: renderer.layout,
    template: { id: connection?.credentials.templateId || "", name: viewName() },
    pending: connection?.pendingCount || 0,
    latency: connection?.metrics || {},
    participants: connection?.participants || [],
    error: connection?.error || "",
    // What the Coast app puts behind its header and pills over this view: a fade, nothing, or a band
    // (docs/views.md#3-manifest).
    chrome: data?.manifest().chrome ?? "fade",
    // How tall the page's content is, which a posted view's sheet takes (docs/views.md#15-views-in-messages).
    height: renderer.contentHeight,
  } satisfies RuntimeMessage;
}
function templateInfo(id: string | undefined) {
  return templates.find((template) => template.id === id);
}
function viewName() {
  return viewTitle(
    connection?.source ? data?.manifest().name : undefined,
    templateInfo(connection?.credentials.templateId),
  );
}
function toast(text: string) {
  $("toast").textContent = text;
  $("toast").hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    $("toast").hidden = true;
  }, 4200);
}
function status() {
  const connected = Boolean(connection?.connected);
  renderer.setConnection(connected || !connection?.lost);
  $("connection").className = connected ? "connected" : connection?.ready ? "disconnected" : "";
  const label = connected
    ? connection?.pendingCount
      ? "Saving…"
      : "Live"
    : connection?.ready
      ? "Offline · saved on device"
      : "Connecting";
  $("connection-label").textContent = label;
  $("connection-label").dataset.short = connected ? (connection?.pendingCount ? "Saving" : "Live") : "Offline";
  $("view-title").textContent = viewName();
  document.title = `${viewName()} · Coast`;
  updateMetrics();
  notifyStatus();
}
function presence() {
  renderer.setParticipants(connection?.participants || []);
  $("participants").replaceChildren();
  for (const person of connection?.participants || []) {
    const item = document.createElement("span");
    item.textContent = person.name
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
    item.style.background = person.color;
    item.title = `${person.name}${person.clientId === connection?.clientID ? " (you)" : ""}${person.editing ? " · editing" : ""}`;
    $("participants").append(item);
  }
  status();
}
/** The element the next request is about, by its selector. `from` tells the Coast app around the view a pick, after which picking goes on. */
function select(selection: Selection | null, from?: "pick") {
  selected = typeof selection?.selector === "string" && selection.selector ? selection : null;
  renderer.selection = selected;
  connection?.select(selected);
  renderer.drawOverlays();
  if (!selected) connection?.editing(false);
  native({ type: "view.selection", selection: selected, ...(from ? { from } : {}) });
}
function setPicking(value: boolean) {
  renderer.setPicking(value);
  if (value) toast("Tap any part of the view to select it.");
}
// A pick does not end picking: the next tap moves the selection until the message is sent.
renderer.onPick = (selection) => {
  select(selection, "pick");
};
// Inside the Coast app, picking is the Coast app's to end.
renderer.onUnpick = () => {
  if (!nativeHost) setPicking(false);
};
renderer.onAppearance = notifyStatus;
renderer.onHeight = notifyStatus;
// A view's copy: the Coast app's pasteboard inside it, the browser's clipboard outside.
renderer.onCopy = (text) => (nativeHost ? native({ type: "view.copy", text }) : navigator.clipboard.writeText(text));
if (nativeHost) renderer.onShowImage = (urls, index) => native({ type: "view.image", urls, index });
// In a browser the tab says whether the page is in view, as the Coast app does with setVisible (Issues #33).
if (!nativeHost)
  document.addEventListener("visibilitychange", () => renderer.setVisible(document.visibilityState === "visible"));
renderer.onLoaded = () => {
  $("boot").hidden = true;
  setOverlayBottom(window.__coastOverlayBottom || 0);
  updateTopClearance();
  status();
};
// The view has drawn: the Coast app shows it now, and not before, so nothing half-built shows first.
renderer.onDrawn = () => {
  $("boot").hidden = true;
  native({ type: "view.drawn", viewId: connection?.credentials.viewId ?? null });
};
// Errors in the view's page go to its object, where its admins' agents read them.
renderer.onPageError = (error) =>
  connection?.reportProblem(
    error.source === "coast" ? { ...error, message: `In Coast's stock kit or page API: ${error.message}` } : error,
  );

function credentialsFromHash(): ViewCredentials | null {
  const params = new URLSearchParams(location.hash.slice(1));
  const viewId = params.get("view");
  const token = params.get("token");
  if (!viewId || !token) return null;
  if (!/^[\w-]{8,128}$/.test(viewId) || !/^[\w-]{20,128}$/.test(token))
    throw new Error("The shared view link is invalid.");
  return { viewId, token, templateId: "" };
}
function remember(credentials: ViewCredentials) {
  store("coast:last-view", credentials);
  const saved = readStored<Record<string, ViewCredentials>>("coast:views", {});
  saved[credentials.templateId] = credentials;
  store("coast:views", saved);
  const views = savedViews();
  views[credentials.viewId] = { ...credentials, updatedAt: new Date().toISOString() };
  store("coast:view-history", views);
  const url = new URL(location.href);
  url.pathname = "/";
  url.search = "";
  url.hash = new URLSearchParams({ view: credentials.viewId, token: credentials.token }).toString();
  history.replaceState(null, "", url);
  publishLibrary();
}
async function createLinkView(templateId: string) {
  const response = await fetch("/api/views", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ templateId }),
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || "The view could not be created.");
  return body as ViewCredentials;
}
/** Opens a view. A view opened with a chat's ticket is not saved as a link: the ticket is only for this device, for a while. */
async function openView(credentials: ViewCredentials, save = true) {
  const currentStartup = ++startup;
  opened = !save;
  panel.close();
  connection?.dispose();
  connection = null;
  data = null;
  selected = null;
  renderer.detach();
  setEditing(false);
  // Blank while a view opens; the boot screen speaks only when opening fails.
  $("boot").hidden = false;
  $("boot").querySelector("span")!.textContent = "";
  $("retry-boot").hidden = true;
  native({ type: "view.selection", selection: null });
  // A view opened with a ticket learns its person from its object, so it works on a copy of the stored identity.
  const next = new ViewConnection(credentials, save ? identity : { ...identity });
  connection = next;
  renderer.identity = { ...next.identity };
  data = new DataHost(next, () => next.source);
  const viewData = data;
  const context = save ? { chat: null, org: null, nav: [], presents: [] } : openedWith;
  next.addEventListener("identity", () => {
    if (connection === next) {
      renderer.identity = { ...next.identity };
      presence();
      notifyStatus();
    }
  });
  next.addEventListener("status", status);
  next.addEventListener("presence", presence);
  next.addEventListener("live", (event) => {
    const { type, data, from } = (event as CustomEvent).detail;
    renderer.receiveLive(type, data, from);
  });
  next.addEventListener("ready", () => {
    if (currentStartup !== startup) return;
    // The page loads once the view's object has said who this is and where its data lives, so `coast.me` and
    // `coast.chat` are right from the start. A device that has been here before knows already; one that cannot reach it
    // waits a moment.
    let shown = false;
    const show = () => {
      if (shown || connection !== next) return;
      shown = true;
      const home = "home" in context ? context.home : undefined;
      renderer.context = {
        view: next.credentials.viewId,
        chat: context.chat,
        org: context.org,
        presents: context.presents,
        ...(home ? { scope: "home" as const, name: home.name, chats: home.chats } : {}),
        ...("reach" in context && context.reach === "posted" ? { reach: "posted" as const } : {}),
        ...("agent" in context && context.agent ? { agent: context.agent } : {}),
      };
      renderer.liveConnection = next;
      renderer.attach(next, viewData, context.nav);
      presence();
    };
    if (next.data) show();
    else {
      next.addEventListener("data-info", show, { once: true });
      setTimeout(show, 1500);
    }
    if (save) remember(next.credentials);
    presence();
  });
  next.addEventListener("message", (event) => handleMessage((event as CustomEvent).detail));
  status();
  try {
    await next.start();
  } catch (error) {
    if (currentStartup !== startup) return;
    const message = error instanceof Error ? error.message : String(error);
    $("boot").querySelector("span")!.textContent = message;
    $("retry-boot").hidden = false;
    native({ type: "view.failed", viewId: credentials.viewId, message });
    status();
  }
}
/** A saved posted view as the Coast app hands it over, checked against the view it opens: its date and its input. */
function frozenOf(value: unknown, view: string): { at: string; input: RenderInput } | null {
  const frozen = value as { at?: unknown; input?: Partial<RenderInput> } | null;
  const input = frozen?.input;
  if (!frozen || typeof frozen.at !== "string" || !input || input.view !== view) return null;
  if (typeof input.source !== "string" || !input.rows || !input.writer || !input.chat) return null;
  return { at: frozen.at, input: input as RenderInput };
}
/**
 * Opens a posted view saved as of a day (docs/views.md#15-views-in-messages): from what its saver read then, with no
 * connection to its object, the same for everyone, refusing every change.
 */
function openFrozen(frozen: { at: string; input: RenderInput }) {
  ++startup;
  opened = true;
  panel.close();
  connection?.dispose();
  connection = null;
  selected = null;
  renderer.detach();
  setEditing(false);
  $("boot").hidden = true;
  native({ type: "view.selection", selection: null });
  const view = new LocalView(frozen.input.source);
  let host: DataHost | null = null;
  const local = new GivenData(frozen.input, () => host!.manifest(), undefined, frozen.at);
  host = new DataHost(local, () => view.source);
  local.setUses(frozen.input.uses);
  data = host;
  renderer.identity = { id: frozen.input.writer.id, name: frozen.input.writer.name, color: colors[0]! };
  renderer.context = {
    view: frozen.input.view,
    chat: { title: frozen.input.chat.title, icon: frozen.input.chat.icon },
    org: openedWith.org,
    presents: [],
    reach: "posted",
  };
  renderer.attach(view, host, []);
  status();
}
async function initialize() {
  const templatesRequest = fetch("/api/templates")
    .then((response) => {
      if (!response.ok) throw new Error("The templates are unavailable.");
      return response.json();
    })
    .then((data) => {
      templates = data.templates;
      store("coast:templates", templates);
      status();
      publishLibrary();
    })
    .catch(() => {
      templates = readStored("coast:templates", []);
      publishLibrary();
    });
  try {
    let credentials =
      credentialsFromHash() || (nativeHost ? null : readStored<ViewCredentials | null>("coast:last-view", null));
    // The Coast app opens views from its own list; inside it, the runtime waits instead of creating one.
    if (!credentials && nativeHost) {
      await templatesRequest;
      return;
    }
    if (!credentials) {
      await templatesRequest;
      const first = templates[0];
      if (!first) throw new Error("No templates are available from this server.");
      credentials = await createLinkView(first.id);
    }
    await openView(credentials);
    await templatesRequest;
  } catch (error) {
    $("boot").querySelector("span")!.textContent = error instanceof Error ? error.message : String(error);
    $("retry-boot").hidden = false;
  }
}
// What the view's object says beyond its data: an error it refused a change with.
function handleMessage(message: { type?: unknown; text?: unknown }) {
  if (message.type === "error") toast(String(message.text || "The view refused a change."));
  status();
}
function openPanel(title: string, kind: string) {
  if (nativeHost) return;
  panelKind = kind;
  $("panel-title").textContent = title;
  $("panel-body").replaceChildren();
  if (!panel.open) panel.showModal();
}
function element<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = "") {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}
function action(label: string, handler: () => void | Promise<void>) {
  const button = element("button", "", label);
  button.type = "button";
  button.addEventListener("click", () =>
    Promise.resolve(handler()).catch((error) => toast(error.message || String(error))),
  );
  return button;
}
function divider() {
  $("panel-body").append(element("div", "panel-divider"));
}
/** A link view's shareable link; a view opened with a chat's ticket has none, since its ticket is the person's own. */
function viewLink() {
  if (opened) return "";
  if (!connection) return location.href;
  return `${location.origin}/#${new URLSearchParams({ view: connection.credentials.viewId, token: connection.credentials.token })}`;
}
async function shareView() {
  if (opened) {
    toast("Share this view from its chat.");
    return;
  }
  try {
    await navigator.clipboard.writeText(viewLink());
    toast("View link copied. Anyone with the link can edit.");
  } catch {
    if (nativeHost) {
      native({ type: "view.share", url: viewLink() });
      return;
    }
    openPanel("Share this view", "share");
    $("panel-body").append(element("p", "panel-subtitle", "Anyone with this link can join and edit the view."));
    const input = element("textarea", "view-link");
    input.value = viewLink();
    input.setAttribute("aria-label", "View link");
    const field = element("label", "field");
    field.append(input);
    $("panel-body").append(field);
    input.select();
  }
}
function savedViews(): Record<string, SavedView> {
  const views = readStored<Record<string, SavedView>>("coast:view-history", {});
  for (const credential of Object.values(readStored<Record<string, ViewCredentials>>("coast:views", {}))) {
    if (!views[credential.viewId]) views[credential.viewId] = credential;
  }
  return views;
}
function library() {
  return {
    type: "view.library",
    templates: templates.map((template) => ({
      id: template.id,
      name: template.name,
      description: template.description || "",
      symbol: template.symbol || "square.stack",
    })),
    views: Object.values(savedViews())
      .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))
      .map((saved) => ({
        viewId: saved.viewId,
        templateId: saved.templateId,
        name: templateInfo(saved.templateId)?.name || saved.templateId,
        symbol: templateInfo(saved.templateId)?.symbol || "square.stack",
        updatedAt: saved.updatedAt || "",
      })),
    currentViewId: connection?.credentials.viewId || null,
    editing,
  } satisfies RuntimeMessage;
}
function publishLibrary() {
  native(library());
}
async function openSavedView(viewId: string) {
  const saved = savedViews()[viewId];
  if (!saved) throw new Error("This view is not saved on this device. Open its shared link to join.");
  await openView(saved);
  return library();
}
async function createView(templateId: string) {
  if (!templates.some((template) => template.id === templateId)) throw new Error("Unknown template.");
  await openView(await createLinkView(templateId));
  return library();
}
async function openTemplate(templateId: string) {
  if (!templates.some((template) => template.id === templateId)) throw new Error("Unknown template.");
  const saved = readStored<Record<string, ViewCredentials>>("coast:views", {});
  await openView(saved[templateId] || (await createLinkView(templateId)));
  return library();
}
function openLibrary() {
  if (nativeHost) {
    publishLibrary();
    return library();
  }
  openPanel("Views & templates", "library");
  $("panel-body").append(
    element(
      "p",
      "panel-subtitle",
      "Open a view, or start a new one from a template. Choose Settings → Edit view when you want to ask the agent for changes.",
    ),
  );
  $("panel-body").append(element("span", "panel-label", "Start from a template"));
  for (const template of templates) {
    const button = action("", async () => {
      button.disabled = true;
      try {
        await createView(template.id);
      } catch (error) {
        button.disabled = false;
        throw error;
      }
    });
    button.className = "library-option";
    const description = element("div");
    description.append(element("strong", "", template.name), element("small", "", template.description || "New view"));
    button.append(description, element("span", "library-arrow", "+"));
    $("panel-body").append(button);
  }
  const listed = library();
  if (listed.views.length) {
    divider();
    $("panel-body").append(element("span", "panel-label", "Your views"));
    for (const saved of listed.views) {
      const button = action("", () => void openSavedView(saved.viewId));
      button.className = "library-option";
      const description = element("div");
      description.append(
        element("strong", "", saved.name),
        element(
          "small",
          "",
          `${saved.viewId.slice(0, 8)}${saved.viewId === listed.currentViewId ? " · Open now" : ""}`,
        ),
      );
      button.append(description, element("span", "library-arrow", saved.viewId === listed.currentViewId ? "✓" : "↗"));
      $("panel-body").append(button);
    }
  }
  divider();
  const profile = element("div", "profile-name");
  const label = element("label", "", "Display name");
  label.htmlFor = "display-name";
  const input = element("input");
  input.id = "display-name";
  input.value = identity.name;
  input.maxLength = 40;
  input.addEventListener("input", () => {
    identity.name = input.value.trim() || "Guest";
    store("coast:identity", identity);
    connection?.rename(identity.name);
    if (connection) renderer.identity = { ...connection.identity };
  });
  profile.append(label, input);
  $("panel-body").append(profile);
  const actions = element("div", "panel-actions");
  actions.append(
    action("Copy view link ↗", shareView),
    action("Latency & connection", openMetrics),
    action("Export view", exportView),
  );
  $("panel-body").append(
    actions,
    element("p", "field-note", "A view link grants editing access. Keep it private or share it with collaborators."),
  );
  return listed;
}
function exportView() {
  if (!connection?.source) return toast("Open a view before exporting it.");
  const data = {
    format: "coast-view",
    schema: 1,
    view: templateInfo(connection.credentials.templateId) ?? {
      id: connection.credentials.templateId,
      name: viewName(),
    },
    source: connection.source,
    revision: connection.revision,
    exportedAt: new Date().toISOString(),
  };
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  const link = element("a");
  link.href = url;
  link.download = `${connection.credentials.templateId}.coast.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  toast("View exported.");
}
function openSettings() {
  if (nativeHost) return;
  openPanel("Settings", "settings");
  const edit = action("Edit view", () => {
    panel.close();
    setEditing(true);
  });
  edit.className = "settings-action primary-button";
  $("panel-body").append(edit);
  $("panel-body").append(
    element("p", "field-note", "Edit mode shows the selection rings; pick an element to select it."),
  );
  divider();
  const actions = element("div", "settings-actions");
  actions.append(
    action("Views & templates", () => {
      openLibrary();
    }),
    action("Copy view link ↗", shareView),
    action("Latency & connection", openMetrics),
    action("Export view", exportView),
  );
  if (editing)
    actions.append(
      action("Finish editing", () => {
        panel.close();
        setEditing(false);
      }),
    );
  $("panel-body").append(actions);
}
function openMetrics() {
  openPanel("Live measurements", "metrics");
  $("panel-body").append(element("p", "panel-subtitle", "Measured in this client."));
  const grid = element("div", "metric-grid");
  for (const [id, label] of [["ping", "Network round trip"]]) {
    const card = element("div", "metric");
    const value = element("strong", "", "—");
    value.id = `metric-${id}`;
    card.append(value, element("small", "", label));
    grid.append(card);
  }
  $("panel-body").append(grid);
  const details = element("p", "field-note");
  details.id = "metric-status";
  $("panel-body").append(details);
  const people = element("div");
  people.id = "metric-people";
  $("panel-body").append(people);
  const actions = element("div", "panel-actions");
  actions.append(
    action("Measure round trip", () => connection?.ping()),
    action("Reconnect", () => {
      if (connection?.socket) connection.socket.close();
      else connection?.connect();
    }),
  );
  $("panel-body").append(actions);
  updateMetrics();
}
function updateMetrics() {
  if (panelKind !== "metrics" || !panel.open || !connection) return;
  $("metric-ping").textContent = metricsValue(connection.metrics.pingMs);
  $("metric-status").textContent =
    `${connection.connected ? "Connected" : "Offline"} · revision ${connection.revision} · ${connection.pendingCount} pending`;
  $("metric-people").replaceChildren();
  for (const person of connection.participants) {
    const row = element("div", "participant-detail");
    const dot = element("i");
    dot.style.background = person.color;
    row.append(
      dot,
      document.createTextNode(`${person.name}${person.clientId === connection.clientID ? " (you)" : ""}`),
      element("span", "", person.editing ? "Editing" : person.selection ? "Selecting" : "Viewing"),
    );
    $("metric-people").append(row);
  }
}
window.coastRuntime = {
  select,
  setEditing,
  setOverlayBottom,
  setOverlayTop,
  setNativeHost,
  openLibrary,
  library,
  // Picking for the Coast app's own control: a tap selects, and the selection comes back as `view.selection`.
  pick: (enabled: unknown) => {
    renderer.setPicking(enabled === true);
    return { picking: renderer.picking };
  },
  /** The Coast app's face, `system` or `geist` (src/shared/fonts.ts), which views are set in. */
  setFont: (face: string) => {
    renderer.setFont(face);
    return { font: renderer.font };
  },
  /**
   * The Coast app's text sizes, `{sizes: {title, figure, body, secondary, detail}, scale}` in points (src/shared/fonts.ts),
   * which views' text follows; it sends them again as the person's text size changes.
   */
  setTextSizes: (text: unknown) => {
    renderer.setTextSizes(text);
    return { ok: true };
  },
  setAccent: (accent: string, onAccent: string) => {
    renderer.setAccent(accent, onAccent);
    return { accent: renderer.accent };
  },
  setFormat: (format: unknown) => {
    renderer.setFormat(format);
    return { format: renderer.format };
  },
  // Whether the page is in view: the Coast app says when a screen covers it or the chat shows another view.
  setVisible: (visible: unknown) => {
    renderer.setVisible(visible === true);
    return { visible: renderer.visible };
  },
  // Refresh in the view's ⋯ menu: the page reads its data again, or is loaded again when it doesn't listen.
  refresh: () => {
    renderer.refresh();
    return { ok: true };
  },
  // `card` for a section of the view a message shows, which the Coast app draws live in the timeline.
  setLayout: (layout: string) => {
    if (layout === "half" || layout === "full" || layout === "desktop" || layout === "card") renderer.setLayout(layout);
    notifyStatus();
    return { layout: renderer.layout };
  },
  openSaved: openSavedView,
  createView,
  openTemplate,
  connectionStatus,
  shareView,
  viewLink,
  // One of a chat's views, with the ticket the Coast app got from the chat, or a view's home with the ticket for it,
  // which names the chats it reads (docs/apple.md#3-views-in-the-app, docs/views.md#1-views).
  // Without a ticket, a view this device has had before shows from the device at once; `ticket` then connects it. It
  // opens at `section`, or at `nav`, a stack of sections, for a screen of its own or a sheet (`presents`, on iPhone).
  open: async (input: BridgeInput<"open">) => {
    if (
      typeof input?.view !== "string" ||
      !/^[a-f0-9]{64}$/.test(input.view) ||
      (input.ticket !== undefined &&
        input.ticket !== null &&
        (typeof input.ticket !== "string" || !/^[\w-]{43}$/.test(input.ticket)))
    )
      throw new Error("Open needs a view, and a ticket if it has one.");
    const home = homeOf(input.home);
    const section = sectionOf(input.section);
    openedWith = {
      chat: home ? null : chatOf(input.chat),
      org: orgOf(input.org),
      nav: Array.isArray(input.nav)
        ? input.nav.slice(0, 20).flatMap((entry) => sectionOf(entry) ?? [])
        : section
          ? [section]
          : [],
      presents: Array.isArray(input.presents)
        ? input.presents.filter((as): as is HostPresentation => as === "screen" || as === "sheet")
        : [],
      ...(home ? { home } : {}),
      ...(input.reach === "posted" ? { reach: "posted" as const } : {}),
      ...agentOf(input.agent),
    };
    const place = home
      ? "home"
      : typeof (input.chat as { id?: unknown } | null)?.id === "string"
        ? (input.chat as { id: string }).id
        : undefined;
    // A posted view saved as of a day opens from what it was saved with, and connects to nothing.
    const frozen = frozenOf(input.frozen, input.view);
    if (frozen) {
      openFrozen(frozen);
      return { view: input.view };
    }
    const early = earlyTicket?.view === input.view ? earlyTicket.ticket : "";
    earlyTicket = null;
    await openView(
      {
        viewId: input.view,
        token: typeof input.ticket === "string" ? input.ticket : early,
        templateId: typeof input.template === "string" ? input.template : "",
        ...(place ? { place } : {}),
      },
      false,
    );
    return { view: input.view };
  },
  // The ticket for the view opened without one, which connects it to its object.
  ticket: (input: BridgeInput<"ticket">) => {
    if (typeof input?.view !== "string" || typeof input.ticket !== "string") return { ok: false };
    if (connection && input.view === connection.credentials.viewId) connection.useTicket(input.ticket);
    // One that comes before its view opens waits for it.
    else earlyTicket = { view: input.view, ticket: input.ticket };
    return { ok: true };
  },
  // A link opened while the view is showing: the section opens on this screen
  // (docs/views.md#5-navigation-sections-and-links).
  openSection: (section: unknown) => {
    const entry = sectionOf(section);
    if (entry) renderer.openSection(entry);
    return { ok: Boolean(entry) };
  },
  // The Coast app's Back: the view's last section closes, or at its start the view asks to leave with `view.back`.
  back: () => {
    renderer.back();
    return { ok: true };
  },
  // Home in the view's ⋯ at its home: back to the view's start from every section open on this screen.
  start: () => {
    renderer.start();
    return { ok: true };
  },
};
// What the view asks of the Coast app around it. In a browser there is none: a link outside opens in a tab.
renderer.onRequest = (request) => {
  if (nativeHost) native(request);
  else if (request.type === "view.open" && /^https?:\/\//.test(request.url))
    window.open(request.url, "_blank", "noopener");
};
/** A view's home as the Coast app opens it: its name and the chats its ticket reads. */
function homeOf(value: unknown): { name: string; chats: NonNullable<PageContext["chats"]> } | null {
  if (!value || typeof value !== "object") return null;
  const { name, chats } = value as Record<string, unknown>;
  if (!Array.isArray(chats)) return null;
  const list = chats.slice(0, 200).flatMap((entry) => {
    const { id, title, subtitle, icon, member } = (entry ?? {}) as Record<string, unknown>;
    return typeof id === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(id)
      ? [
          {
            id,
            title: typeof title === "string" ? title.slice(0, 120) : "",
            subtitle: typeof subtitle === "string" && subtitle ? subtitle.slice(0, 80) : null,
            icon: iconFromApp(icon),
            member: member === true,
          },
        ]
      : [];
  });
  return { name: typeof name === "string" ? name.slice(0, 120) : "", chats: list };
}
/** The person's own Agent as the Coast app names it, where they have one to ask; nothing otherwise. */
function agentOf(value: unknown): { agent: { name: string } } | Record<string, never> {
  const name = (value as { name?: unknown } | null)?.name;
  return typeof name === "string" && name.trim() ? { agent: { name: name.trim().slice(0, 40) } } : {};
}
function chatOf(value: unknown): PageContext["chat"] {
  if (!value || typeof value !== "object") return null;
  const { title, icon } = value as Record<string, unknown>;
  return typeof title === "string" ? { title: title.slice(0, 80), icon: iconFromApp(icon) } : null;
}
function orgOf(value: unknown): PageContext["org"] {
  if (!value || typeof value !== "object") return null;
  const { id, name } = value as Record<string, unknown>;
  return typeof id === "string" && typeof name === "string" ? { id: id.slice(0, 64), name: name.slice(0, 80) } : null;
}
function sectionOf(value: unknown): NavEntry | null {
  if (!value || typeof value !== "object") return null;
  const { name, params, title } = value as Record<string, unknown>;
  if (typeof name !== "string" || !/^[a-z][a-z0-9_-]{0,39}$/.test(name)) return null;
  const entry: NavEntry = { name, params: {} };
  if (params && typeof params === "object")
    for (const [key, item] of Object.entries(params))
      if (typeof item === "string" || typeof item === "number") entry.params[key] = String(item);
  if (typeof title === "string") entry.title = title.slice(0, 80);
  const { id } = value as Record<string, unknown>;
  if (typeof id === "string" && /^[A-Za-z0-9_-]{1,24}$/.test(id)) entry.id = id;
  return entry;
}
// Back closes the section the view has open, as the Coast app's Back does
// (docs/views.md#5-navigation-sections-and-links), then the editor, then opens the list.
$("back").addEventListener("click", () => {
  if (editing) setEditing(false);
  else if (renderer.navDepth) renderer.back();
  else openLibrary();
});
$("settings").addEventListener("click", openSettings);
$("connection").addEventListener("click", openMetrics);
$("close-panel").addEventListener("click", () => panel.close());
panel.addEventListener("close", () => {
  panelKind = "";
});
$("retry-boot").addEventListener("click", () => {
  if (connection?.credentials) void openView(connection.credentials, !opened);
  else void initialize();
});
window.addEventListener("hashchange", () => {
  try {
    const credentials = credentialsFromHash();
    if (credentials && credentials.viewId !== connection?.credentials.viewId) void openView(credentials);
  } catch (error) {
    toast(String(error));
  }
});
window.addEventListener("online", () => connection?.connect());
window.addEventListener("keydown", (event) => {
  if (event.key === "Escape") setPicking(false);
});
new ResizeObserver(updateTopClearance).observe($("toolbar"));
window.addEventListener("resize", updateTopClearance);
setNativeHost(nativeHost);
setEditing(editing);
void initialize();
